import React, { useState, useEffect, useCallback } from 'react';
import { 
  Scale, FileText, CheckCircle, AlertCircle, Clock, Globe, 
  ChevronDown, ChevronUp, Plus, Building2, Receipt,
  Shield, BookOpen, Users, Banknote, AlertTriangle, Ticket, ThumbsUp, Paperclip, Link2
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLang } from '../../context/LanguageContext';
import { API_BASE as API } from '../../lib/apiBase';
import { toast } from 'sonner';


export default function LegalReviewPanel({ advisorActions = false }) {
  const { token, user } = useAuth();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({ checklist: true, commissions: true });
  const [noteForm, setNoteForm] = useState({ text: '', category: 'general', priority: 'media' });
  const [savingNote, setSavingNote] = useState(false);
  const [signoffNotes, setSignoffNotes] = useState('');
  const [signingOff, setSigningOff] = useState(false);
  const [ticketTitle, setTicketTitle] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [ticketCategory, setTicketCategory] = useState('general');
  const [ticketChecklistId, setTicketChecklistId] = useState('');
  const [ticketFiles, setTicketFiles] = useState(null);
  const [openingTicket, setOpeningTicket] = useState(false);
  const [expandedTicketId, setExpandedTicketId] = useState(null);
  /** @type {Record<string, { text: string, files: FileList | null }>} */
  const [advisorReplyByTicket, setAdvisorReplyByTicket] = useState({});
  const [replyingId, setReplyingId] = useState(null);
  const [adminReplyText, setAdminReplyText] = useState({});
  const [adminReplyFiles, setAdminReplyFiles] = useState({});
  const [adminStatusNote, setAdminStatusNote] = useState({});

  const showAdvisorBar = advisorActions && user?.role === 'legal_advisor';
  const isAdminTeam = user?.role === 'admin' || user?.role === 'pluka';

  const fileUrl = (u) => (u && u.startsWith('http')) ? u : `${API}${u}`;

  const updateTicketStatus = async (ticketId, status) => {
    try {
      const note = (adminStatusNote[ticketId] || '').trim();
      const res = await fetch(`${API}/api/admin/fiscal/legal-tickets/${ticketId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status, internal_note: note || undefined }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Error');
        return;
      }
      toast.success(lang === 'es' ? 'Ticket actualizado' : 'Ticket updated');
      setAdminStatusNote((m) => ({ ...m, [ticketId]: '' }));
      fetchData();
    } catch {
      toast.error(lang === 'es' ? 'Error de red' : 'Network error');
    }
  };

  const sendTicketReply = async (ticketId, isAdminSide) => {
    const draft = advisorReplyByTicket[ticketId] || { text: '', files: null };
    const text = isAdminSide ? (adminReplyText[ticketId] || '').trim() : (draft.text || '').trim();
    const rawFiles = isAdminSide ? adminReplyFiles[ticketId] : draft.files;
    const fileList = rawFiles && rawFiles.length ? Array.from(rawFiles) : [];
    if (!text && fileList.length === 0) {
      toast.error(lang === 'es' ? 'Escribe un mensaje o adjunta un archivo' : 'Add a message or attachment');
      return;
    }
    setReplyingId(ticketId);
    try {
      const fd = new FormData();
      fd.append('body', text || (fileList.length ? (lang === 'es' ? '(adjuntos)' : '(attachments)') : ''));
      fileList.forEach((f) => fd.append('files', f));
      const res = await fetch(`${API}/api/fiscal/legal-review/tickets/${ticketId}/messages`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const err = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(err.detail || 'Error');
        setReplyingId(null);
        return;
      }
      if (isAdminSide) {
        setAdminReplyText((m) => ({ ...m, [ticketId]: '' }));
        setAdminReplyFiles((m) => ({ ...m, [ticketId]: null }));
      } else {
        setAdvisorReplyByTicket((m) => ({ ...m, [ticketId]: { text: '', files: null } }));
      }
      toast.success(lang === 'es' ? 'Mensaje enviado' : 'Message sent');
      fetchData();
    } catch {
      toast.error(lang === 'es' ? 'Error de red' : 'Network error');
    }
    setReplyingId(null);
  };

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/fiscal/legal-review`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSection = (s) => setExpandedSections(prev => ({ ...prev, [s]: !prev[s] }));

  const addNote = async () => {
    if (!noteForm.text.trim()) return;
    setSavingNote(true);
    try {
      await fetch(`${API}/api/fiscal/legal-review/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(noteForm),
      });
      setNoteForm({ text: '', category: 'general', priority: 'media' });
      fetchData();
    } catch (e) { console.error(e); }
    setSavingNote(false);
  };

  const submitSignOff = async () => {
    setSigningOff(true);
    try {
      const res = await fetch(`${API}/api/fiscal/legal-review/sign-off`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          notes: signoffNotes,
          scope: 'legal_review_panel_snapshot',
        }),
      });
      const err = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(err.detail || (lang === 'es' ? 'No se pudo registrar' : 'Could not save'));
        setSigningOff(false);
        return;
      }
      setSignoffNotes('');
      toast.success(lang === 'es' ? 'Conformidad registrada. El equipo ha sido notificado.' : 'Sign-off recorded. Team notified.');
      fetchData();
    } catch {
      toast.error(lang === 'es' ? 'Error de red' : 'Network error');
    }
    setSigningOff(false);
  };

  const submitTicket = async () => {
    if (!ticketTitle.trim() || !ticketDescription.trim()) {
      toast.error(lang === 'es' ? 'Título y descripción obligatorios' : 'Title and description required');
      return;
    }
    setOpeningTicket(true);
    try {
      const fd = new FormData();
      fd.append('title', ticketTitle.trim());
      fd.append('description', ticketDescription.trim());
      fd.append('category', ticketCategory);
      if (ticketChecklistId) fd.append('checklist_item_id', ticketChecklistId);
      if (ticketFiles && ticketFiles.length) {
        Array.from(ticketFiles).forEach((f) => fd.append('files', f));
      }
      const res = await fetch(`${API}/api/fiscal/legal-review/tickets`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const err = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(err.detail || (lang === 'es' ? 'No se pudo abrir el ticket' : 'Could not open ticket'));
        setOpeningTicket(false);
        return;
      }
      setTicketTitle('');
      setTicketDescription('');
      setTicketChecklistId('');
      setTicketFiles(null);
      toast.success(lang === 'es' ? 'Ticket enviado al equipo.' : 'Ticket sent to the team.');
      fetchData();
    } catch {
      toast.error(lang === 'es' ? 'Error de red' : 'Network error');
    }
    setOpeningTicket(false);
  };

  if (loading) return <div className="text-center py-8 text-pulse-muted">Cargando panel legal...</div>;
  if (!data) return <div className="text-center py-8 text-red-400">Error al cargar datos legales</div>;

  const statusIcon = (status) => {
    if (status === 'implementado') return <CheckCircle size={16} className="text-green-400" />;
    if (status === 'configurado' || status === 'estimacion_orientativa') return <AlertCircle size={16} className="text-amber-400" />;
    return <Clock size={16} className="text-red-400" />;
  };

  const priorityColor = (p) => {
    if (p === 'critica') return 'text-red-500 bg-red-500/10 border-red-500/20';
    if (p === 'alta') return 'text-red-400 bg-red-400/10 border-red-400/20';
    if (p === 'media') return 'text-amber-400 bg-amber-400/10 border-amber-400/20';
    return 'text-slate-400 bg-slate-400/10 border-slate-400/20';
  };

  return (
    <div className={`space-y-6 ${showAdvisorBar ? 'pb-52' : ''}`} data-testid="legal-review-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800/80 to-slate-700/60 rounded-xl p-6 border border-slate-600/30">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
          <div className="flex items-center gap-3">
            <Scale size={24} className="text-blue-400" />
            <h2 className="text-xl font-bold text-white">Panel de Revision Legal</h2>
          </div>
          {typeof data?.legal_tickets_open_count === 'number' && data.legal_tickets_open_count > 0 && (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full bg-amber-500/15 text-amber-200 border border-amber-500/30 px-3 py-1">
              <Ticket size={14} />
              {lang === 'es' ? 'Tickets abiertos:' : 'Open tickets:'} {data.legal_tickets_open_count}
            </span>
          )}
        </div>
        <p className="text-slate-400 text-sm">
          Vista completa de la configuracion fiscal, comisiones y cumplimiento legal. 
          Disenado para revision por asesor externo.
        </p>
      </div>

      {/* Advisor: sign-off + tickets (external counsel only) */}
      {showAdvisorBar && (
        <div
          className="rounded-xl border border-emerald-500/30 bg-emerald-950/30 p-4 space-y-4"
          data-testid="legal-advisor-actions"
        >
          <p className="text-xs text-emerald-200/90 leading-relaxed">
            {lang === 'es'
              ? 'Tras revisar este panel, registre su conformidad o abra un ticket para el equipo interno. Esto deja constancia en el sistema para la auditoría previa al lanzamiento; no sustituye un informe jurídico por escrito si su colegio lo exige.'
              : 'After reviewing this panel, record your sign-off or open a ticket for the internal team. This creates an audit trail before launch; it does not replace a formal legal opinion where required.'}
          </p>
          {data?.latest_signoff && (
            <div className="rounded-lg bg-black/30 border border-white/10 px-3 py-2 text-xs text-slate-300">
              <span className="text-emerald-400 font-semibold">
                {lang === 'es' ? 'Última conformidad:' : 'Last sign-off:'}
              </span>{' '}
              {new Date(data.latest_signoff.created_at).toLocaleString()}{' '}
              — {data.latest_signoff.advisor_name}
              {data.latest_signoff.notes && (
                <span className="block mt-1 text-slate-400">{data.latest_signoff.notes}</span>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-emerald-500/20 bg-black/20 p-3 space-y-2">
              <div className="flex items-center gap-2 text-emerald-300 text-sm font-semibold">
                <ThumbsUp size={16} /> {lang === 'es' ? 'Correcto / Conforme' : 'Correct / Sign-off'}
              </div>
              <textarea
                value={signoffNotes}
                onChange={(e) => setSignoffNotes(e.target.value)}
                placeholder={lang === 'es' ? 'Nota breve (opcional)' : 'Short note (optional)'}
                rows={2}
                className="w-full bg-slate-900/80 border border-slate-600 rounded-lg p-2 text-xs text-white placeholder-slate-500"
              />
              <button
                type="button"
                onClick={submitSignOff}
                disabled={signingOff}
                className="w-full rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold py-2.5 disabled:opacity-50"
                data-testid="legal-signoff-btn"
              >
                {signingOff ? '…' : lang === 'es' ? 'Registrar conformidad' : 'Record sign-off'}
              </button>
            </div>
            <div className="rounded-lg border border-cyan-500/20 bg-black/20 p-3 space-y-2">
              <div className="flex items-center gap-2 text-cyan-300 text-sm font-semibold">
                <Ticket size={16} /> {lang === 'es' ? 'Abrir ticket' : 'Open ticket'}
              </div>
              <p className="text-[10px] text-slate-500 leading-relaxed">
                {lang === 'es'
                  ? 'Describa qué está bien o qué debe corregirse; puede enlazar un ítem del checklist y adjuntar PDF o imagen.'
                  : 'Describe what is OK or what must change; link a checklist item and attach PDF or images.'}
              </p>
              <input
                value={ticketTitle}
                onChange={(e) => setTicketTitle(e.target.value)}
                placeholder={lang === 'es' ? 'Asunto' : 'Subject'}
                className="w-full bg-slate-900/80 border border-slate-600 rounded-lg px-2 py-1.5 text-xs text-white"
              />
              <label className="flex items-center gap-1 text-[10px] text-slate-400">
                <Link2 size={12} />
                {lang === 'es' ? 'Ítem checklist (opc.)' : 'Checklist item (opt.)'}
              </label>
              <select
                value={ticketChecklistId}
                onChange={(e) => setTicketChecklistId(e.target.value)}
                className="w-full bg-slate-900/80 border border-slate-600 rounded-lg px-2 py-1.5 text-xs text-white"
              >
                <option value="">{lang === 'es' ? '— Ninguno —' : '— None —'}</option>
                {(data.legal_checklist || []).map((row) => (
                  <option key={row.id || row.item} value={row.id}>
                    {row.item}
                  </option>
                ))}
              </select>
              <textarea
                value={ticketDescription}
                onChange={(e) => setTicketDescription(e.target.value)}
                placeholder={lang === 'es' ? 'Informe detallado: qué revisar, qué falta, referencias…' : 'Detailed note: what to review, gaps, refs…'}
                rows={5}
                className="w-full bg-slate-900/80 border border-slate-600 rounded-lg p-2 text-xs text-white placeholder-slate-500"
              />
              <select
                value={ticketCategory}
                onChange={(e) => setTicketCategory(e.target.value)}
                className="w-full bg-slate-900/80 border border-slate-600 rounded-lg px-2 py-1.5 text-xs text-white"
              >
                <option value="general">General</option>
                <option value="fiscal">Fiscal</option>
                <option value="contratos">Contratos</option>
                <option value="rgpd">RGPD / datos</option>
                <option value="propiedad_intelectual">Propiedad intelectual</option>
              </select>
              <div>
                <input
                  id="legal-ticket-files"
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                  onChange={(e) => setTicketFiles(e.target.files)}
                />
                <label
                  htmlFor="legal-ticket-files"
                  className="inline-flex items-center gap-2 text-[11px] text-cyan-400/90 cursor-pointer hover:underline"
                >
                  <Paperclip size={14} />
                  {lang === 'es' ? 'Adjuntos (PDF, imágenes, doc/docx — máx. 12MB c/u)' : 'Attachments (PDF, images, doc/docx — max 12MB each)'}
                </label>
              </div>
              {ticketFiles && ticketFiles.length > 0 && (
                <p className="text-[10px] text-cyan-400/90">{Array.from(ticketFiles).map((f) => f.name).join(', ')}</p>
              )}
              <button
                type="button"
                onClick={submitTicket}
                disabled={openingTicket}
                className="w-full rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold py-2.5 disabled:opacity-50"
                data-testid="legal-ticket-btn"
              >
                {openingTicket ? '…' : lang === 'es' ? 'Enviar ticket al equipo' : 'Send ticket to team'}
              </button>
            </div>
          </div>
          {data?.legal_tickets?.length > 0 && (
            <div className="border-t border-white/10 pt-3">
              <p className="text-xs font-semibold text-slate-400 mb-2">
                {lang === 'es' ? 'Tickets y conversación' : 'Tickets & thread'}
              </p>
              <ul className="space-y-3 max-h-[28rem] overflow-y-auto">
                {data.legal_tickets.map((t) => {
                  const checklistLabel = t.checklist_item_id
                    ? (data.legal_checklist || []).find((c) => c.id === t.checklist_item_id)?.item
                    : null;
                  const open = expandedTicketId === t.id;
                  return (
                    <li key={t.id} className="rounded-lg bg-slate-900/50 border border-slate-700/50 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setExpandedTicketId(open ? null : t.id)}
                        className="w-full text-left px-3 py-2 flex justify-between gap-2 items-start hover:bg-slate-800/50"
                      >
                        <span className="text-slate-200 font-medium text-xs">{t.title}</span>
                        <span
                          className={`shrink-0 uppercase text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            t.status === 'closed' ? 'bg-slate-600/40 text-slate-300' : 'bg-amber-500/20 text-amber-200'
                          }`}
                        >
                          {t.status}
                        </span>
                      </button>
                      {checklistLabel && (
                        <p className="px-3 text-[10px] text-cyan-400/90 flex items-center gap-1">
                          <Link2 size={10} /> {checklistLabel}
                        </p>
                      )}
                      {open && (
                        <div className="px-3 pb-3 space-y-2 border-t border-slate-700/40">
                          <div className="space-y-2 max-h-56 overflow-y-auto mt-2">
                            {(t.messages || []).map((m) => (
                              <div
                                key={m.id}
                                className={`rounded-lg p-2 text-[11px] ${
                                  m.role === 'legal_advisor'
                                    ? 'bg-cyan-950/40 border border-cyan-800/30'
                                    : 'bg-slate-800/60 border border-slate-600/30'
                                }`}
                              >
                                <p className="text-slate-500 text-[10px] mb-1">
                                  {m.author_name} · {m.role} · {new Date(m.created_at).toLocaleString()}
                                </p>
                                <p className="text-slate-200 whitespace-pre-wrap">{m.body}</p>
                                {m.attachments?.length > 0 && (
                                  <ul className="mt-1 space-y-0.5">
                                    {m.attachments.map((a) => (
                                      <li key={a.id}>
                                        <a
                                          href={fileUrl(a.url)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-cyan-400 underline"
                                        >
                                          {a.filename}
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            ))}
                          </div>
                          <textarea
                            value={advisorReplyByTicket[t.id]?.text || ''}
                            onChange={(e) =>
                              setAdvisorReplyByTicket((m) => ({
                                ...m,
                                [t.id]: { text: e.target.value, files: m[t.id]?.files || null },
                              }))
                            }
                            placeholder={lang === 'es' ? 'Responder al equipo…' : 'Reply to team…'}
                            rows={2}
                            className="w-full bg-slate-950/80 border border-slate-600 rounded-lg p-2 text-xs text-white"
                          />
                          <label className="flex items-center gap-2 text-[10px] text-slate-500 cursor-pointer">
                            <Paperclip size={12} />
                            <input
                              type="file"
                              multiple
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                              className="text-xs"
                              onChange={(e) =>
                                setAdvisorReplyByTicket((m) => ({
                                  ...m,
                                  [t.id]: { text: m[t.id]?.text || '', files: e.target.files },
                                }))
                              }
                            />
                          </label>
                          <button
                            type="button"
                            disabled={replyingId === t.id}
                            onClick={() => sendTicketReply(t.id, false)}
                            className="w-full rounded-lg bg-slate-600 hover:bg-slate-500 text-white text-xs py-2"
                          >
                            {replyingId === t.id ? '…' : lang === 'es' ? 'Enviar respuesta' : 'Send reply'}
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {isAdminTeam && data?.legal_tickets?.length > 0 && (
        <div className="rounded-xl border border-slate-600/40 bg-slate-900/40 p-4" data-testid="legal-tickets-admin">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Ticket size={16} className="text-cyan-400" />
            {lang === 'es' ? 'Tickets legales (equipo)' : 'Legal tickets (team)'}
          </h3>
          <p className="text-[11px] text-slate-500 mb-3">
            {lang === 'es'
              ? 'Responda en el hilo para el asesor; al cambiar estado puede añadir nota interna (queda en el hilo).'
              : 'Reply in the thread for counsel; when changing status you can add a note (stored in the thread).'}
          </p>
          <ul className="space-y-4">
            {data.legal_tickets.map((t) => (
              <li key={t.id} className="rounded-lg border border-slate-700/50 p-3 text-xs space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-white font-medium">{t.title}</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <textarea
                      value={adminStatusNote[t.id] ?? ''}
                      onChange={(e) => setAdminStatusNote((m) => ({ ...m, [t.id]: e.target.value }))}
                      placeholder={lang === 'es' ? 'Nota al cambiar estado (opc.)' : 'Note when changing status (opt.)'}
                      rows={1}
                      className="bg-slate-950/80 border border-slate-600 rounded px-2 py-1 text-[11px] text-white min-w-[140px]"
                    />
                    <select
                      value={t.status}
                      onChange={(e) => updateTicketStatus(t.id, e.target.value)}
                      className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-[11px] text-white"
                    >
                      <option value="open">open</option>
                      <option value="in_progress">in_progress</option>
                      <option value="closed">closed</option>
                      <option value="wontfix">wontfix</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto border border-slate-700/40 rounded-lg p-2 bg-black/20">
                  {(t.messages || []).map((m) => (
                    <div key={m.id} className="rounded p-2 bg-slate-800/50 text-[11px]">
                      <p className="text-slate-500 text-[10px]">{m.author_name} · {m.role}</p>
                      <p className="text-slate-200 whitespace-pre-wrap">{m.body}</p>
                      {m.attachments?.length > 0 && (
                        <ul className="mt-1">
                          {m.attachments.map((a) => (
                            <li key={a.id}>
                              <a href={fileUrl(a.url)} target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline">
                                {a.filename}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
                <textarea
                  value={adminReplyText[t.id] ?? ''}
                  onChange={(e) => setAdminReplyText((m) => ({ ...m, [t.id]: e.target.value }))}
                  placeholder={lang === 'es' ? 'Respuesta al asesor…' : 'Reply to advisor…'}
                  rows={2}
                  className="w-full bg-slate-950/80 border border-slate-600 rounded-lg p-2 text-xs text-white"
                />
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                    className="text-[10px] text-slate-400"
                    onChange={(e) => setAdminReplyFiles((m) => ({ ...m, [t.id]: e.target.files }))}
                  />
                  <button
                    type="button"
                    disabled={replyingId === t.id}
                    onClick={() => sendTicketReply(t.id, true)}
                    className="rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-[11px] px-3 py-1.5"
                  >
                    {replyingId === t.id ? '…' : lang === 'es' ? 'Enviar al asesor' : 'Send to advisor'}
                  </button>
                </div>
                <p className="text-slate-600">{t.advisor_name} · {t.category}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Global Legal Disclaimer */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4" data-testid="legal-disclaimer-banner">
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-300 mb-1">Aviso Legal Importante</p>
            <p className="text-xs text-amber-200/80">
              Este panel contiene informacion orientativa y estimaciones fiscales basadas en legislacion publica general. 
              <strong> NO constituye asesoramiento fiscal ni legal.</strong> Toda la configuracion fiscal requiere validacion 
              por un profesional cualificado. Los informes de liquidacion emitidos son borradores sin validez fiscal 
              hasta la constitucion formal de la entidad y alta en Hacienda.
            </p>
          </div>
        </div>
      </div>

      {/* Platform Info */}
      <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
        <div className="flex items-center gap-2 mb-4">
          <Building2 size={18} className="text-blue-400" />
          <h3 className="font-semibold text-white">Datos de la Plataforma</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {Object.entries(data.platform_info).map(([k, v]) => (
            <div key={k} className="flex justify-between bg-slate-900/40 rounded-lg p-3">
              <span className="text-slate-400 capitalize">{k.replace(/_/g, ' ')}</span>
              <span className="text-white font-medium">{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Commission Models */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
        <button onClick={() => toggleSection('commissions')} 
                className="w-full flex items-center justify-between p-5 hover:bg-slate-700/30 transition"
                data-testid="toggle-commissions">
          <div className="flex items-center gap-2">
            <Banknote size={18} className="text-green-400" />
            <h3 className="font-semibold text-white">Modelos de Comision por Vertical</h3>
          </div>
          {expandedSections.commissions ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
        </button>
        {expandedSections.commissions && (
          <div className="px-5 pb-5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="commission-table">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-700">
                    <th className="pb-3 pr-4">Vertical</th>
                    <th className="pb-3 pr-4 text-center">Plataforma</th>
                    <th className="pb-3 pr-4 text-center">Pluka</th>
                    <th className="pb-3 pr-4 text-center">PulseStockIA</th>
                    <th className="pb-3 pr-4 text-center">Artista</th>
                    <th className="pb-3 text-center">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.commission_models).map(([key, m]) => (
                    <tr key={key} className="border-b border-slate-700/50">
                      <td className="py-3 pr-4">
                        <span className="text-white font-medium">{m.label}</span>
                        <span className="block text-xs text-slate-500">{m.invoice_label}</span>
                      </td>
                      <td className="py-3 pr-4 text-center">
                        <span className="bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded text-xs font-bold">{m.platform.pct}%</span>
                      </td>
                      <td className="py-3 pr-4 text-center">
                        {m.pluka.pct > 0 
                          ? <span className="bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded text-xs font-bold">{m.pluka.pct}%</span>
                          : <span className="text-slate-600">-</span>}
                      </td>
                      <td className="py-3 pr-4 text-center">
                        {m.pulsestockia.pct > 0 
                          ? <span className="bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded text-xs font-bold">{m.pulsestockia.pct}%</span>
                          : <span className="text-slate-600">-</span>}
                      </td>
                      <td className="py-3 pr-4 text-center">
                        <span className="bg-green-500/20 text-green-300 px-2 py-0.5 rounded text-xs font-bold">
                          {m.artist_pct || m.reserve_pct || 0}%
                        </span>
                        {m.reserve_pct && <span className="block text-xs text-slate-500 mt-0.5">ahorro</span>}
                      </td>
                      <td className="py-3 text-center text-slate-300 font-medium">{m.total_commission_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 p-3 bg-slate-900/40 rounded-lg">
              <p className="text-xs text-slate-500">
                <strong>Nombres en informe:</strong> Pulse Infrastructure Fee (plataforma) | 
                Artist Management Fee (Pluka) | Ecosystem Growth Fund (PulseStockIA)
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Tax Rates by Country */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
        <button onClick={() => toggleSection('taxes')} 
                className="w-full flex items-center justify-between p-5 hover:bg-slate-700/30 transition"
                data-testid="toggle-taxes">
          <div className="flex items-center gap-2">
            <Globe size={18} className="text-cyan-400" />
            <h3 className="font-semibold text-white">Tasas Fiscales por Pais ({Object.keys(data.tax_rates_by_country).length} paises)</h3>
          </div>
          {expandedSections.taxes ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
        </button>
        {expandedSections.taxes && (
          <div className="px-5 pb-5">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-700">
                    <th className="pb-3 pr-4">Pais</th>
                    <th className="pb-3 pr-4 text-center">Retencion</th>
                    <th className="pb-3 pr-4 text-center">IVA/VAT</th>
                    <th className="pb-3 pr-4">SGAE</th>
                    <th className="pb-3">Nota legal</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.tax_rates_by_country).filter(([k]) => k !== 'DEFAULT').map(([code, tax]) => (
                    <tr key={code} className="border-b border-slate-700/30">
                      <td className="py-3 pr-4">
                        <span className="text-white font-medium">{code}</span>
                        <span className="block text-xs text-slate-500">{tax.name}</span>
                      </td>
                      <td className="py-3 pr-4 text-center">
                        <span className="text-amber-300 font-bold">{tax.withholding_pct || 0}%</span>
                        {tax.withholding_reduced_pct && (
                          <span className="block text-xs text-slate-500">{tax.withholding_reduced_pct}% reducido</span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-center">
                        <span className="text-cyan-300 font-bold">{tax.vat_pct || 0}%</span>
                        {tax.vat_reduced_pct && (
                          <span className="block text-xs text-slate-500">{tax.vat_reduced_pct}% reducido</span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        {tax.sgae_available 
                          ? <span className="text-green-400 text-xs">Disponible ({tax.sgae_pct}%)</span>
                          : <span className="text-slate-600 text-xs">N/A</span>}
                      </td>
                      <td className="py-3">
                        <span className="text-xs text-slate-400 line-clamp-2">{tax.withholding_note || '-'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Legal refs for key countries */}
            <div className="mt-4 space-y-2">
              <h4 className="text-sm font-semibold text-slate-300">Referencias legales clave:</h4>
              {['ES', 'US', 'GB'].map(code => {
                const tax = data.tax_rates_by_country[code];
                if (!tax?.legal_refs) return null;
                return (
                  <div key={code} className="bg-slate-900/40 rounded-lg p-3">
                    <span className="text-xs font-bold text-slate-300">{tax.name}:</span>
                    <ul className="mt-1 space-y-0.5">
                      {tax.legal_refs.map((ref, i) => (
                        <li key={i} className="text-xs text-slate-500 flex items-start gap-1">
                          <BookOpen size={10} className="mt-0.5 shrink-0 text-slate-600" />
                          {ref}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Withdrawal Policy */}
      <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
        <div className="flex items-center gap-2 mb-3">
          <Receipt size={18} className="text-green-400" />
          <h3 className="font-semibold text-white">Politica de Retirada (Liquidacion)</h3>
        </div>
        <div className="bg-slate-900/40 rounded-lg p-4 text-sm">
          <p className="text-white mb-2">{data.withdrawal_policy.description}</p>
          <p className="text-slate-400 text-xs">{data.withdrawal_policy.legal_note}</p>
        </div>
      </div>

      {/* Legal Checklist */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
        <button onClick={() => toggleSection('checklist')} 
                className="w-full flex items-center justify-between p-5 hover:bg-slate-700/30 transition"
                data-testid="toggle-checklist">
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-red-400" />
            <h3 className="font-semibold text-white">
              Checklist Legal ({data.legal_checklist.filter(c => c.status === 'pendiente').length} pendientes)
            </h3>
          </div>
          {expandedSections.checklist ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
        </button>
        {expandedSections.checklist && (
          <div className="px-5 pb-5 space-y-2">
            {data.legal_checklist.map((item, i) => (
              <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${
                item.status === 'pendiente' ? 'bg-slate-900/40 border-slate-700/30' : 
                item.status === 'estimacion_orientativa' ? 'bg-amber-900/10 border-amber-700/20' :
                'bg-green-900/10 border-green-700/20'
              }`}>
                {statusIcon(item.status)}
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-white font-medium">{item.item}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-bold uppercase ${priorityColor(item.priority)}`}>
                      {item.priority}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{item.note}</p>
                  {item.legal_ref && <p className="text-[10px] text-slate-600 mt-0.5 italic">{item.legal_ref}</p>}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${
                  item.status === 'implementado' ? 'bg-green-500/20 text-green-300' :
                  item.status === 'estimacion_orientativa' ? 'bg-amber-500/20 text-amber-300' :
                  item.priority === 'critica' ? 'bg-red-500/20 text-red-300' :
                  'bg-slate-500/20 text-slate-300'
                }`}>{item.status === 'estimacion_orientativa' ? 'solo estimacion' : item.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sample Invoices */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
        <button onClick={() => toggleSection('invoices')} 
                className="w-full flex items-center justify-between p-5 hover:bg-slate-700/30 transition"
                data-testid="toggle-invoices">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-purple-400" />
            <h3 className="font-semibold text-white">Informes de Liquidacion (Borrador — sin validez fiscal)</h3>
          </div>
          {expandedSections.invoices ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
        </button>
        {expandedSections.invoices && (
          <div className="px-5 pb-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(data.sample_invoices).map(([region, inv]) => (
              <div key={region} className="bg-slate-900/60 rounded-lg p-4 border border-slate-700/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-white uppercase">{region}</span>
                  <span className="text-xs text-slate-500">{inv.invoice_number}</span>
                </div>
                {inv.disclaimer && (
                  <p className="text-[10px] text-amber-400/80 mb-2 italic bg-amber-500/5 rounded p-1.5">{inv.disclaimer}</p>
                )}
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">Subtotal</span><span className="text-white">{inv.subtotal} {inv.currency}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">IVA ({inv.vat_pct}%)</span><span className="text-cyan-300">+{inv.vat_amount} {inv.currency}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Retencion ({inv.withholding_pct}%)</span><span className="text-amber-300">-{inv.withholding_amount} {inv.currency}</span></div>
                  {inv.sgae_pct > 0 && <div className="flex justify-between"><span className="text-slate-400">SGAE ({inv.sgae_pct}%)</span><span className="text-red-300">-{inv.sgae_amount} {inv.currency}</span></div>}
                  <hr className="border-slate-700" />
                  <div className="flex justify-between font-bold"><span className="text-slate-300">TOTAL</span><span className="text-white">{inv.total} {inv.currency}</span></div>
                </div>
                {inv.legal_refs?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-700/30">
                    <span className="text-[10px] text-slate-600">Refs: {inv.legal_refs.join(' | ')}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tax Profiles Stats */}
      <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
        <div className="flex items-center gap-2 mb-3">
          <Users size={18} className="text-purple-400" />
          <h3 className="font-semibold text-white">Perfiles Fiscales</h3>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-slate-900/40 rounded-lg p-3 text-center">
            <span className="text-2xl font-bold text-white">{data.tax_profiles_stats.total}</span>
            <span className="block text-xs text-slate-500 mt-1">Total</span>
          </div>
          <div className="bg-slate-900/40 rounded-lg p-3 text-center">
            <span className="text-2xl font-bold text-green-400">{data.tax_profiles_stats.verified}</span>
            <span className="block text-xs text-slate-500 mt-1">Verificados</span>
          </div>
          <div className="bg-slate-900/40 rounded-lg p-3 text-center">
            <span className="text-2xl font-bold text-amber-400">{data.tax_profiles_stats.unverified}</span>
            <span className="block text-xs text-slate-500 mt-1">Pendientes</span>
          </div>
        </div>
      </div>

      {/* Advisor Notes */}
      <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle size={18} className="text-amber-400" />
          <h3 className="font-semibold text-white">Notas del Asesor</h3>
        </div>
        
        {/* Add note form */}
        <div className="bg-slate-900/40 rounded-lg p-4 mb-4">
          <textarea
            value={noteForm.text}
            onChange={e => setNoteForm({ ...noteForm, text: e.target.value })}
            placeholder="Escribir nota para revision legal..."
            className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-sm text-white placeholder-slate-500 resize-none"
            rows={3}
            data-testid="legal-note-input"
          />
          <div className="flex items-center gap-3 mt-2">
            <select value={noteForm.category} onChange={e => setNoteForm({ ...noteForm, category: e.target.value })}
                    className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white">
              <option value="general">General</option>
              <option value="fiscal">Fiscal</option>
              <option value="comisiones">Comisiones</option>
              <option value="facturacion">Facturacion</option>
              <option value="rgpd">RGPD</option>
              <option value="contratos">Contratos</option>
            </select>
            <select value={noteForm.priority} onChange={e => setNoteForm({ ...noteForm, priority: e.target.value })}
                    className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white">
              <option value="alta">Alta</option>
              <option value="media">Media</option>
              <option value="baja">Baja</option>
            </select>
            <button onClick={addNote} disabled={savingNote || !noteForm.text.trim()}
                    className="ml-auto flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white text-xs px-3 py-1.5 rounded disabled:opacity-50 transition"
                    data-testid="add-legal-note-btn">
              {savingNote ? <Clock size={12} className="animate-spin" /> : <Plus size={12} />}
              Agregar nota
            </button>
          </div>
        </div>

        {/* Existing notes */}
        {data.advisor_notes?.length > 0 ? (
          <div className="space-y-2">
            {data.advisor_notes.map(note => (
              <div key={note.id} className={`p-3 rounded-lg border ${priorityColor(note.priority)}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold uppercase">{note.category}</span>
                  <span className="text-[10px] text-slate-500">{new Date(note.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm text-white">{note.text}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-4">No hay notas de revision todavia</p>
        )}
      </div>
    </div>
  );
}
